import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle2, LifeBuoy, Loader2, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useLang } from '../i18n/LangContext';

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const API = process.env.REACT_APP_BACKEND_URL;

const SupportPage = () => {
  const { t } = useLang();
  const s = t.footer.support;
  useEffect(() => {
    document.title = `${s.title} | KRUG — Pronađi svoj krug`;
    return () => {
      document.title = 'KRUG — Pronađi svoj krug | Aplikacija za dijasporu';
    };
  }, [s.title]);
  const [form, setForm] = useState({ name: '', email: '', topic: s.topics[0], message: '', website: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (form.name.trim().length < 2) er.name = s.errors.name;
    if (!EMAIL_RE.test(form.email.trim())) er.email = s.errors.email;
    if (form.message.trim().length < 10) er.message = s.errors.message;
    setErrors(er);
    if (Object.keys(er).length) return;

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setDone(true);
    } catch {
      toast.error(s.failToast);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    'w-full rounded-xl border border-white/15 bg-white/[0.05] px-4 py-3.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-krug-tealbright transition-colors duration-300';
  const labelCls = 'block text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-2';

  return (
    <div data-testid="support-page" className="min-h-screen bg-krug-navy">
      <Navbar />
      <main className="max-w-6xl mx-auto px-5 md:px-8 pt-32 md:pt-36 pb-24">
        <div className="max-w-2xl">
          <p className="font-heading text-xs md:text-sm font-bold tracking-[0.3em] uppercase text-krug-tealbright mb-5 flex items-center gap-2.5">
            <LifeBuoy size={16} className="text-krug-goldbright" />
            KRUG
          </p>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-white">{s.title}</h1>
          <p className="mt-4 text-base md:text-lg text-slate-400">{s.sub}</p>
          <a
            href="mailto:info@krug-app.eu"
            data-testid="support-email-link"
            className="mt-7 inline-flex items-center gap-3 rounded-2xl glass-dark px-6 py-4 hover:border-krug-gold/50 transition-colors duration-300"
          >
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-krug-gold/15 text-krug-goldbright">
              <Mail size={19} />
            </span>
            <span>
              <span className="block text-[10px] uppercase tracking-widest text-slate-500">{s.emailTitle}</span>
              <span className="block font-heading text-lg font-extrabold text-white">info@krug-app.eu</span>
            </span>
          </a>
        </div>

        <div className="mt-14 grid lg:grid-cols-[1.1fr_0.9fr] gap-10">
          <div className="rounded-[2rem] glass-dark p-7 md:p-9">
            <h2 className="font-heading text-xl md:text-2xl font-extrabold text-white">{s.formTitle}</h2>
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="ok"
                  data-testid="support-success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-12 text-center"
                >
                  <span className="mx-auto inline-flex items-center justify-center w-14 h-14 rounded-full bg-krug-teal/15 text-krug-tealbright">
                    <CheckCircle2 size={28} />
                  </span>
                  <h3 className="mt-5 font-heading text-2xl font-extrabold text-white">{s.successTitle}</h3>
                  <p className="mt-2.5 text-sm text-slate-400">{s.successMsg}</p>
                </motion.div>
              ) : (
                <motion.form key="form" data-testid="support-form" onSubmit={submit} noValidate className="mt-6 space-y-5">
                  <input
                    type="text"
                    value={form.website}
                    onChange={set('website')}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                  />
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="sup-name" className={labelCls}>{s.labels.name}</label>
                      <input id="sup-name" data-testid="support-input-name" value={form.name} onChange={set('name')} className={`${inputCls} ${errors.name ? 'border-red-400/70' : ''}`} />
                      {errors.name && <p data-testid="support-error-name" className="mt-1.5 text-xs text-red-400">{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="sup-email" className={labelCls}>{s.labels.email}</label>
                      <input id="sup-email" type="email" data-testid="support-input-email" value={form.email} onChange={set('email')} className={`${inputCls} ${errors.email ? 'border-red-400/70' : ''}`} />
                      {errors.email && <p data-testid="support-error-email" className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="sup-topic" className={labelCls}>{s.labels.topic}</label>
                    <select
                      id="sup-topic"
                      data-testid="support-topic-select"
                      value={form.topic}
                      onChange={set('topic')}
                      className={`${inputCls} appearance-none bg-krug-surface`}
                    >
                      {s.topics.map((tp) => (
                        <option key={tp} value={tp}>{tp}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="sup-message" className={labelCls}>{s.labels.message}</label>
                    <textarea
                      id="sup-message"
                      rows={5}
                      data-testid="support-input-message"
                      placeholder={s.placeholderMessage}
                      value={form.message}
                      onChange={set('message')}
                      className={`${inputCls} resize-none ${errors.message ? 'border-red-400/70' : ''}`}
                    />
                    {errors.message && <p data-testid="support-error-message" className="mt-1.5 text-xs text-red-400">{errors.message}</p>}
                  </div>
                  <button
                    type="submit"
                    data-testid="support-submit"
                    disabled={loading}
                    className="group inline-flex items-center gap-2 rounded-full bg-krug-gold px-8 py-3.5 font-heading font-bold text-krug-navy hover:bg-krug-goldbright disabled:opacity-60 transition-colors duration-300"
                  >
                    {loading ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />}
                    {s.submit}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>

          <div>
            <h2 className="font-heading text-xl md:text-2xl font-extrabold text-white">{s.helpTitle}</h2>
            <div className="mt-6 space-y-4">
              {s.help.map((h, i) => (
                <div key={h.t} data-testid={`support-help-${i}`} className="rounded-2xl glass-dark p-6 hover:border-krug-tealbright/40 transition-colors duration-300">
                  <h3 className="font-heading text-base font-extrabold text-white">{h.t}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{h.d}</p>
                  {h.link && (
                    <a href={h.link} data-testid={`support-help-link-${i}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-krug-tealbright hover:text-krug-goldbright transition-colors duration-300">
                      {h.linkLabel}
                      <ArrowRight size={14} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SupportPage;
